import {
  booleanAttribute, ChangeDetectorRef, Component, ContentChild, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output,
  TemplateRef
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isObservable, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';

/** Where suggestions come from: a remote search for the typed text, or a list filtered here. */
export type TypeaheadSource<T> = readonly T[] | ((q: string) => Observable<T[]>);

/** The most suggestions a local list offers. */
export const TYPEAHEAD_MAX_LOCAL = 10;

/**
 * Filters a local list for the typed text: case-insensitive "contains" on each item's display text, the items that
 * start with it first (otherwise in list order), at most `max`.
 */
export function filterLocal<T>(items: readonly T[], q: string, displayWith: (item: T) => string,
  max: number = TYPEAHEAD_MAX_LOCAL): T[] {
  const term = (q ?? '').trim().toLowerCase();
  if (!term) {
    return [];
  }
  const starts: T[] = [];
  const contains: T[] = [];
  for (const item of items ?? []) {
    const text = (displayWith(item) ?? '').toLowerCase();
    const at = text.indexOf(term);
    if (at === 0) {
      starts.push(item);
    } else if (at > 0) {
      contains.push(item);
    }
  }
  return [...starts, ...contains].slice(0, max);
}

let nextId = 0;

/**
 * A text box that suggests matches as it is typed into: a combobox with a listbox of options.
 *
 * It is a ControlValueAccessor over the box's TEXT, so it works with ngModel and formControlName, and a screen whose
 * search matches on text keeps working unchanged: choosing a suggestion puts its text in the box (`valueWith`, or
 * `displayWith` when that is not given), exactly as if it had been typed, and emits the item as `(picked)`.
 *
 * - Suggestions come from `source`: a remote search, called once typing pauses for 250 ms with the latest text of
 *   `minLength` (2) characters or more, or a local list filtered here (see filterLocal). An answer that arrives
 *   after newer typing is dropped, and a failed search (or a 404) suggests nothing.
 * - Up and Down move through the suggestions, Enter chooses the highlighted one (and only then: with nothing
 *   highlighted Enter does what it did before), Escape and leaving the box close the list.
 * - `enabled` false renders the plain text box with no suggestions, no listbox and no combobox attributes, so a
 *   screen behaves exactly as it did before its switch was on.
 *
 * The box takes `inputId` (for an external `<label for>`), `name`, `placeholder` and `inputClass`; key and focus
 * events bubble to the host element, so `(keydown.enter)` on `<app-typeahead>` still works. Each option shows
 * `displayWith` in bold and `secondaryWith` beneath, unless an `<ng-template let-item>` is given as content.
 */
@Component({
  selector: 'app-typeahead',
  templateUrl: './typeahead.component.html',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet],
  host: { class: 'tw:block tw:relative' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadComponent),
      multi: true,
    },
  ],
})
export class TypeaheadComponent<T = any> implements ControlValueAccessor, OnInit, OnDestroy {

  /** How long typing must pause before a search. */
  public static readonly debounceMs = 250;

  @Input() source: TypeaheadSource<T> = [];
  @Input() displayWith: (item: T) => string = item => item == null ? '' : String(item);
  /** The small second line of an option, if any. */
  @Input() secondaryWith: (item: T) => string;
  /** The text a pick puts in the box; displayWith when not given. */
  @Input() valueWith: (item: T) => string;
  @Input() minLength: number = 2;
  @Input() placeholder: string = '';
  @Input() inputId: string = `typeahead-${nextId++}`;
  @Input() name: string;
  @Input() inputClass: string = 'field__control';
  /** The listbox's accessible name. */
  @Input() listLabel: string = 'Suggestions';
  /** data-test on the box, the listbox and each option. */
  @Input() inputTestId: string = 'typeahead';
  @Input() listTestId: string = 'typeahead-suggestions';
  @Input() optionTestId: string = 'typeahead-suggestion';
  /** Off: a plain text box, no suggestions. */
  @Input({ transform: booleanAttribute }) enabled: boolean = true;
  /** Called the first time the box is focused while enabled, to warm a source (e.g. fetch a list). */
  @Input() prefetch: () => void;

  /** The chosen suggestion, after its text is in the box. */
  @Output() picked = new EventEmitter<T>();

  @ContentChild(TemplateRef) optionTemplate: TemplateRef<{ $implicit: T; index: number }>;

  public value: string = '';
  public disabled: boolean = false;
  public suggestions: T[] = [];
  public open: boolean = false;
  /** The highlighted suggestion, or -1 for none. */
  public active: number = -1;

  private readonly query = new Subject<string | null>();
  private subscription: Subscription;
  private prefetched: boolean = false;
  private choseByEnter: boolean = false;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private changeDetector: ChangeDetectorRef) { }

  public get listboxId(): string {
    return `${this.inputId}-listbox`;
  }

  ngOnInit(): void {
    // null cancels: a search still waiting, or one in flight, cannot reopen the list.
    this.subscription = this.query.pipe(
      debounceTime(TypeaheadComponent.debounceMs),
      switchMap(text => text == null || text.length < this.minLength ? of([] as T[]) : this.find(text))
    ).subscribe(found => {
      this.suggestions = found ?? [];
      this.open = this.enabled && this.suggestions.length > 0;
      this.active = -1;
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private find(text: string): Observable<T[]> {
    const source = this.source;
    if (typeof source === 'function') {
      const result = source(text);
      return (isObservable(result) ? result : of([] as T[])).pipe(catchError(() => of([] as T[])));
    }
    return of(filterLocal(source ?? [], text, this.displayWith));
  }

  public onInput(value: string): void {
    this.value = value ?? '';
    this.onChange(this.value);
    if (!this.enabled) {
      return;
    }
    const text = this.value.trim();
    if (text.length < this.minLength) {
      this.close();
    }
    this.query.next(text);
  }

  public onFocus(): void {
    if (this.enabled && !this.prefetched && this.prefetch) {
      this.prefetched = true;
      this.prefetch();
    }
  }

  public onBlur(): void {
    this.close();
    this.query.next(null);
    this.onTouched();
  }

  public onKeydown(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }
    const count = this.suggestions.length;
    switch (event.key) {
      case 'ArrowDown':
        if (count) {
          event.preventDefault();
          this.open = true;
          this.active = Math.min(count - 1, this.active + 1);
        }
        break;
      case 'ArrowUp':
        if (this.open && count) {
          event.preventDefault();
          this.active = Math.max(0, this.active - 1);
        }
        break;
      case 'Enter':
        if (this.open && this.active >= 0) {
          // Choose, rather than submit a form or run the host's Enter handler.
          event.preventDefault();
          event.stopPropagation();
          this.choseByEnter = true;
          this.choose(this.suggestions[this.active]);
        }
        break;
      case 'Escape':
        if (this.open) {
          event.preventDefault();
          this.close();
        }
        break;
    }
  }

  /** The keyup of the Enter that chose a suggestion stays here too, so a host's (keyup.enter) does not run. */
  public onKeyup(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.choseByEnter) {
      this.choseByEnter = false;
      event.stopPropagation();
    }
  }

  /** A suggestion was chosen: its text goes in the box, as if typed, and it is emitted. */
  public choose(item: T): void {
    this.value = (this.valueWith ?? this.displayWith)(item) ?? '';
    this.onChange(this.value);
    this.close();
    this.query.next(null);
    this.picked.emit(item);
    this.changeDetector.markForCheck();
  }

  public close(): void {
    this.open = false;
    this.active = -1;
  }

  /** The id of an option, for aria-activedescendant. */
  public optionId(index: number): string {
    return `${this.inputId}-option-${index}`;
  }

  public secondary(item: T): string {
    return this.secondaryWith ? (this.secondaryWith(item) ?? '') : '';
  }

  // ControlValueAccessor
  public writeValue(value: any): void {
    this.value = value == null ? '' : String(value);
    this.changeDetector.markForCheck();
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetector.markForCheck();
  }
}
